import React from "react";
import { Table, Badge, Button } from "reactstrap";
import { TableSkeleton, EmptyState } from "../../Components/Common/DashboardKit";
import { statusTone, priorityTone } from "./supportConstants";

const fmt = (d) =>
    d ? new Date(d).toLocaleDateString("en-US", {
        month: "short", day: "numeric", year: "numeric",
    }) : "—";

/**
 * Ticket list table. Pure presentation — the parent owns data + paging.
 * States handled: loading (skeleton), error, empty, and populated with a
 * "Load more" control when the backend reports HasMore.
 */
const SupportTicketList = ({
    tickets = [],
    loading = false,
    error = null,
    page = 1,
    hasMore = false,
    total = null,
    onOpen,
    onPrev,
    onNext,
}) => {
    if (loading) {
        return <TableSkeleton rows={8} columns={8} />;
    }

    if (error) {
        return (
            <EmptyState
                icon="ri-error-warning-line"
                title="Could not load tickets"
                message={error}
                tone="danger"
            />
        );
    }

    if (!tickets.length) {
        return (
            <EmptyState
                icon="ri-inbox-line"
                title="No tickets found"
                message="Try a different account, clear your filters, or create a ticket."
                tone="secondary"
            />
        );
    }

    return (
        <>
            <div className="table-responsive">
                <Table className="align-middle table-hover mb-0">
                    <thead className="table-light">
                        <tr>
                            <th>#</th>
                            <th>Subject</th>
                            <th>Requester</th>
                            <th>Status</th>
                            <th>Priority</th>
                            <th>Group</th>
                            <th>Agent</th>
                            <th>Updated</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tickets.map((t) => (
                            <tr
                                key={`${t.accountKey}-${t.id}`}
                                style={{ cursor: "pointer" }}
                                onClick={() => onOpen?.(t)}
                            >
                                <td className="fw-medium">{t.id}</td>
                                <td>{t.subject}</td>
                                <td>
                                    <div>{t.requesterName || "—"}</div>
                                    {t.requesterEmail && (
                                        <div className="text-muted small">{t.requesterEmail}</div>
                                    )}
                                </td>
                                <td><Badge color={statusTone(t.statusLabel)} pill>{t.statusLabel}</Badge></td>
                                <td><Badge color={priorityTone(t.priorityLabel)} pill>{t.priorityLabel}</Badge></td>
                                <td>{t.groupName || "—"}</td>
                                <td>{t.agentName || "—"}</td>
                                <td className="text-muted">{fmt(t.updatedAt)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            {(page > 1 || hasMore) && (
                <div className="d-flex align-items-center justify-content-between mt-3">
                    <div className="text-muted small">
                        Page {page}
                        {typeof total === "number" ? ` · ${total} matching ticket${total === 1 ? "" : "s"}` : ""}
                    </div>
                    <div className="d-flex gap-2">
                        <Button color="light" onClick={onPrev} disabled={page <= 1}>
                            ‹ Previous
                        </Button>
                        <Button color="light" onClick={onNext} disabled={!hasMore}>
                            Next ›
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

export default SupportTicketList;
